import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from './icons';
import { unitMeta } from './units';
import { openExternalUrl, isNativeApp } from './platform';
import { BookCover, ProgressRing, Flame, StreakTrail, Lockable } from './common';
import { ShareButton } from './share-card';
import { OverlayStoryModal, BotaoOverlayStory } from './overlay-story';
import { BookV2, StreakData, ProfileV2 } from '../types';
import { api } from '../services/apiV2';
import { StoriesProvider, StoriesTray, StoriesOverlays } from './clubs/stories';
import { EstadoHabito } from './habito/api';
import { HeroProtocolo } from './habito/hero';
import { ComoVoceEstaIndo } from './habito/diagnostico';
import { MarcoDoMiolo } from './habito/marco';
import { ritmoRecente, velocidadePlausivel } from './ritmo';
import { BadgeMentor } from './mentor-livro/trilha';

// Paleta curada pros segmentos da pizza — todas dentro do espectro warm
// (ember/golden/wine/ochre/rose/copper) pra ficar coerente com a brand,
// mas com hues bem separados pra cada livro ser visualmente distinto.
// Lightness ~0.6 + chroma ~0.13 garante contraste ok em fundo claro E escuro
// (hero "dayDone" troca pra ink — cores precisam aparecer nos dois).
const BOOK_PALETTE = [
  'oklch(0.62 0.16 40)',   // ember (primary, brand)
  'oklch(0.68 0.14 75)',   // amber/gold
  'oklch(0.55 0.13 18)',   // wine/burgundy
  'oklch(0.62 0.12 95)',   // ochre/mustard
  'oklch(0.58 0.11 355)',  // dusty rose
  'oklch(0.60 0.13 25)',   // copper
];

// Cor por posição no anel — deterministic + sem colisões dentro da mesma view.
// Primeira posição = ember (alinha com look default do anel single).
function colorForBook(_book: BookV2, index: number): string {
  return BOOK_PALETTE[index % BOOK_PALETTE.length];
}

const FALLBACK_QUOTE = {
  text: 'A leitura é para o espírito o que o exercício é para o corpo.',
  author: 'Joseph Addison',
};

export const HomeScreen: React.FC<{
  // Protocolo do Hábito — quando existe programa, o estado do dia é quem manda
  // no herói da Home. O resto da página segue igual (navegar a estante não é o
  // momento da execução, e portanto não é lugar de "zero decisões").
  habito?: EstadoHabito | null;
  onAbrirLivroDoProtocolo?: () => void;
  onVerLivro?: (bookId: string) => void;
  onComecarProtocolo?: () => void;
  onVerPrograma?: () => void;
  onMarcoVisto?: (chave: string) => void;
  onEditarContrato?: () => void;
  onTrocarLivro?: () => void;
  ofereceTroca?: boolean;
  profile: ProfileV2 | null;
  books: BookV2[];
  streak: StreakData | null;
  totals: { books: number; pages: number; hours: number };
  dayPagesRead: number;
  dayTotalGoal: number;
  goalViewUnit?: 'pages' | 'chapters' | 'positions';
  onGoalViewUnit?: (u: 'pages' | 'chapters' | 'positions') => void;
  unitsPresent?: string[];
  dayDone: boolean;
  dailyDoneFor: Record<string, boolean>;
  today: string;
  isPro: boolean;
  maxReading: number;
  dailyQuote?: { text: string; author: string; book?: { title: string } | null } | null;
  // Clubes do user que têm livro ativo — renderizados em seção própria.
  clubsWithBooks?: any[];
  clubsTerm?: 'clube' | 'grupo';
  onOpenClub?: (clubId: string) => void;
  onOpenProfile?: (uid: string) => void;
  onClubBookRead?: (clubId: string) => void;
  onClubActions?: (club: any, mirror: BookV2 | null) => void;
  onExploreClubs?: () => void;
  onOpenBook: (b: BookV2) => void;
  onRead: (b: BookV2, modo?: 'linear' | 'foco') => void;
  onStudy?: (b: BookV2) => void;
  /** Notas do Mentor liberadas e não abertas, por livro (o número vermelho do caderno). */
  mentorNovas?: Record<string, number>;
  /** Livros com A Travessia (o mapa da trilha): o card ganha a entrada e o número vai para ela. */
  travessias?: Record<string, { nao_vistos: number }>;
  onTravessia?: (b: BookV2) => void;
  onBookActions?: (b: BookV2) => void;
  onOpenReport: () => void;
  onOpenPaywall: () => void;
  onOpenNotif: () => void;
  onOpenSupport?: () => void;
  onOpenLibrary: () => void;
  /** Badge de não-lidas no sino. Quando > 0, dot ember aparece. */
  notifUnreadCount?: number;
  /** Onboarding props pra estados vazios + checklist (user novo). */
  onAddBook?: () => void;
  session?: any;
  onActivatePush?: () => void;
  pushSubscribed?: boolean;
  hasAnyReading?: boolean;   // true se user já registrou ao menos 1 sessão
  /** A primeira carga ainda não terminou: esqueleto, não o estado vazio (UX-25). */
  carregando?: boolean;
  /** O nome digitado no cadastro, enquanto o perfil não chega (UX-35). */
  nomeProvisorio?: string | null;
  /** Círculo do Mentor: abre /mentor/:slug (o cartão some se não há Mentor). */
  onAbrirCirculo?: (slug: string) => void;
  /** Só para quem é Mentor (profile.is_mentor): o cartão "Meu Círculo" abre o editor. */
  onAbrirMeuCirculo?: () => void;
}> = ({
  carregando = false,
  nomeProvisorio = null,
  habito = null, onAbrirLivroDoProtocolo, onVerLivro, onComecarProtocolo, onVerPrograma, onMarcoVisto, onEditarContrato, onTrocarLivro, ofereceTroca,
  profile, books, streak, totals, session,
  dayPagesRead, dayTotalGoal, goalViewUnit = 'pages', onGoalViewUnit, unitsPresent = [], dayDone, dailyDoneFor, today,
  isPro, maxReading, dailyQuote,
  clubsWithBooks = [], clubsTerm = 'clube', onOpenClub, onOpenProfile, onClubBookRead, onClubActions, onExploreClubs,
  onOpenBook, onRead, onStudy, mentorNovas, travessias, onTravessia, onBookActions, onOpenReport, onOpenPaywall, onOpenNotif, onOpenSupport, onOpenLibrary,
  onAddBook, onActivatePush, pushSubscribed = false, hasAnyReading = false,
  notifUnreadCount = 0, onAbrirCirculo, onAbrirMeuCirculo,
}) => {
  // O cartão "Meu Círculo" (no topo) já leva o Mentor ao que é dele: a lista
  // dos Círculos lá embaixo deixa de repetir o próprio.
  const meuCirculo = !!(onAbrirMeuCirculo && (profile as any)?.is_mentor);
  // HM4 — convidar amigos: compartilha o link do perfil (share nativo no app,
  // navigator.share na web, fallback abrindo o perfil público).
  const inviteFriends = async () => {
    const handle = (profile as any)?.username;
    const url = handle ? `https://forjamente.com.br/@${handle}` : 'https://forjamente.com.br';
    const text = 'Tô lendo no Forjamente — bora ler juntos? 📚';
    try {
      const mod: any = await import('@capacitor/share').catch(() => null);
      if (mod?.Share?.share) { await mod.Share.share({ title: 'Forjamente', text, url, dialogTitle: 'Convidar amigos' }); return; }
    } catch {}
    try { if ((navigator as any).share) { await (navigator as any).share({ title: 'Forjamente', text, url }); return; } } catch {}
    openExternalUrl(url);
  };

  const active = books.filter(b => b.status === 'reading');

  // O livro do card principal é o do ÚLTIMO REGISTRO, não o primeiro da lista.
  //
  // É o que faz o card acompanhar a pessoa: registrou leitura de outro livro,
  // o card passa a ser dele. Ordena pelo timestamp da sessão mais recente
  // (`reading_date`, que tem hora; `date` só tem o dia e empata quem leu dois
  // livros no mesmo dia). Sem nenhum registro, cai no primeiro em leitura —
  // melhor oferecer algo que exigir que a pessoa vá procurar.
  const livroDoCard = useMemo(() => {
    const quando = (b: any) => {
      const ss = b?.sessions || [];
      let max = '';
      for (const x of ss) { const t = x.reading_date || x.date || ''; if (t > max) max = t; }
      return max;
    };
    const comRegistro = active.filter(b => quando(b));
    if (comRegistro.length) {
      return [...comRegistro].sort((a, b) => (quando(b) > quando(a) ? 1 : -1))[0];
    }
    return active[0] || null;
  }, [active]);
  // Onda 4: rótulo do card de metas segue a unidade de visão selecionada.
  const gu = unitMeta(goalViewUnit);
  // Livros pessoais "puros" (não vinculados a um clube). Os do clube já são exibidos
  // na seção "Lendo com gente" — evita duplicação.
  const personalActive = active.filter(b => !(b as any).fromClubId);
  // UX-5: lock ESTÁVEL. Os `maxReading` livros de started_at mais antigo ficam
  // liberados; o resto trava. Antes o lock seguia a posição na lista (updated_at),
  // então o livro travado "pulava" sozinho ao registrar leitura em outro.
  const unlockedReadingIds = new Set(
    isPro
      ? personalActive.map(b => b.id)
      : [...personalActive]
          .sort((a, b) => (((a as any).startedAt || '') + '').localeCompare(((b as any).startedAt || '') + '') || ((a.id || '') + '').localeCompare((b.id || '') + ''))
          .slice(0, maxReading)
          .map(b => b.id)
  );
  const todayPct = Math.min(1, dayPagesRead / (dayTotalGoal || 1));
  const quote = dailyQuote || FALLBACK_QUOTE;
  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? 'Bom dia' : hour < 18 ? 'Boa tarde' : 'Boa noite';
  const displayName = profile?.display_name?.split(' ')[0] || (nomeProvisorio || '').split(' ')[0] || 'leitor';

  const dateStr = now.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });

  const todayState: 'off' | 'burning' | 'roaring' =
    dayDone || dayPagesRead >= dayTotalGoal ? 'roaring' :
    dayPagesRead > 0 ? 'burning' : 'off';

  const currentStreak = streak?.current ?? 0;

  // Weekly pulse — last 7 days (Mon..Sun)
  const last7 = (streak?.history || []).slice(-7);
  const weekGoal = dayTotalGoal * 7;
  // Pulso semanal. Em unidade única (todos os livros na mesma unidade — incl. o
  // caso atual de 100% páginas) usamos a soma autoritativa do streak: comportamento
  // idêntico ao anterior. Em multi-unidade, somar páginas+capítulos+posições não faz
  // sentido, então recalculamos a partir das sessões SÓ da unidade selecionada, pros
  // 7 mesmos dias — assim numerador e denominador ficam na mesma unidade.
  const multiUnit = unitsPresent.length > 1;
  const last7Keys = last7.map((d: any) => d.date).filter(Boolean);
  const weekDoneUnit = books
    .filter(b => (((b as any).progressUnit) || 'pages') === goalViewUnit)
    .reduce((s, b) => s + (b.sessions || [])
      .filter(x => last7Keys.includes(x.date))
      .reduce((t, x) => t + (x.pages || 0), 0), 0);
  const weekDone = multiUnit ? weekDoneUnit : last7.reduce((s, d) => s + (d.pages || 0), 0);

  // Estado "user novo" — sem livros pessoais nem em clubes.
  // Usado pra mostrar empty states com CTA + esconder elementos vazios.
  const isFirstTime = personalActive.length === 0 && clubsWithBooks.length === 0 && !hasAnyReading;

  // Protocolo do Hábito. Os estados de preparo (qualificar/preparar) não entram
  // aqui porque são atendidos pelo fluxo em tela cheia — quando eles aparecem, a
  // Home nem chega a ser vista.
  // 'LEITOR' e 'DISPENSADO' ficam de fora de propósito: nesses dois a Home é a
  // antiga, inteira. A chave lá em cima é que decide de que lado estamos.
  // EXPLORANDO saiu daqui. Quem respondeu "ainda estou olhando" não entrou em
  // programa nenhum — e trocar a Home dela pelo card "você está livre por aqui"
  // apagava o anel de meta, a chama, "Esta semana" e a meta dos livros. Um
  // usuário antigo que só foi curioso ficava com uma Home PIOR do que tinha.
  //
  // FUSÃO (12/09/2026) — existe UMA Home. Não há mais "card antigo" e "card
  // novo": todo mundo vê o card do dia, com a capa, o livro do último registro
  // e a ação. Inscrito, pausado, explorando, dispensado ou nunca inscrito —
  // o mesmo card. O resto da página (anel, metas, cadência, estante) segue
  // idêntico logo abaixo.
  //
  // Ficam de fora só os dois estados de PREPARO, e não por escolha de layout:
  // neles o app está em fluxo de tela cheia e a Home nem chega a ser vista.
  //
  // Quem não tem programa não tem check-in — e a ofensiva do card nasce dos
  // check-ins. Por isso o card recebe abaixo os números do app como rede: sem
  // eles, sete pessoas que leem aqui veriam a própria chama apagada.
  const mostraProtocolo = !!habito
    && !['PRECISA_QUALIFICAR', 'PRECISA_PREPARAR'].includes(habito.estado);

  // Os dias em que a pessoa leu, pela contabilidade do app. Serve de fonte para
  // a semana e para a sequência quando o protocolo não tem as suas.
  const diasLidosNoApp = (streak?.history || []).filter(d => (d.pages || 0) > 0).map(d => d.date);

  // Estado do anel: contraído (single arc ember) vs expandido (pizza por livro + legenda).
  // Toggle ao clicar no ring/texto interno. Animação fica nas próprias transitions de stroke.
  const [ringExpanded, setRingExpanded] = useState(false);
  const [overlayAberto, setOverlayAberto] = useState(false);

  // spinTurns acumula voltas: incrementa a cada vez que o user EXPANDE. Como o
  // CSS interpola transform linearmente, ir de N voltas pra N+1 vira uma rotação
  // completa clockwise (visual de progresso fluido). Ao colapsar, mantemos o
  // valor — só o scale shrinka, sem spin reverso.
  const [spinTurns, setSpinTurns] = useState(0);
  const toggleRing = () => {
    setRingExpanded(prev => {
      if (!prev) setSpinTurns(t => t + 1); // só conta no expand
      return !prev;
    });
  };

  // Composição do anel quando expandido — quanto cada livro contribuiu HOJE.
  // Apenas livros lidos hoje entram (pagesToday > 0). Cada segmento = pagesToday / dayTotalGoal.
  // Soma >= 1 cropada no ProgressRing (evita overlap quando user lê acima da meta).
  const ringSegments = (() => {
    const denom = dayTotalGoal || 1;
    const items: { book: BookV2; pagesToday: number; color: string }[] = [];
    books.filter(b => b.status === 'reading' && (((b as any).progressUnit) || 'pages') === goalViewUnit).forEach((b, i) => {
      const pagesToday = (b.sessions || [])
        .filter(s => s.date === today)
        .reduce((t, s) => t + (s.pages || 0), 0);
      if (pagesToday > 0) {
        items.push({ book: b, pagesToday, color: colorForBook(b, i) });
      }
    });
    // Ordena maior leitura primeiro (visual: segmento mais grosso aparece logo no topo)
    items.sort((a, b) => b.pagesToday - a.pagesToday);
    return {
      list: items,
      segments: items.map(it => ({ value: it.pagesToday / denom, color: it.color })),
    };
  })();
  const hasMultipleBooks = ringSegments.list.length >= 2;

  // ANTES DE OS DADOS CHEGAREM (pente fino, UX-25): a Home mostrava por um
  // instante o estado de quem não tem livro ("Adicione seu primeiro livro"),
  // e quem tem livro via a tela errada piscar. Até a primeira carga, esqueleto.
  if (carregando && isFirstTime) {
    const bloco = (h: number, w: string | number = '100%', r = 14, extra: React.CSSProperties = {}) => (
      <div style={{ height: h, width: w, borderRadius: r, background: 'var(--paper-2)', animation: 'homeSk 1.4s ease-in-out infinite', ...extra }} />
    );
    return (
      <div className="scroll" data-home-esqueleto="1" aria-busy="true" style={{ paddingBottom: 110 }}>
        <style>{`@keyframes homeSk { 0%,100% { opacity: .55 } 50% { opacity: 1 } }`}</style>
        <div style={{ padding: '40px 20px 0', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>{bloco(42, 42, 10)}{bloco(14, 120, 6)}</div>
          {bloco(26, '62%', 8, { marginTop: 18 })}
          {bloco(170, '100%', 22, { marginTop: 8 })}
          {bloco(120, '100%', 20)}
          {bloco(90, '100%', 20)}
        </div>
      </div>
    );
  }

  // Onboarding agora é o wizard de criação (perfil + notificações testadas + 1º
  // livro) + tutoriais por página, disparados no App.tsx. O checklist de "próximos
  // passos" e o card "clubes públicos" no início saíram daqui.

  return (
    <div className="scroll" style={{ paddingBottom: 110 }}>
      <div style={{ padding: '40px 20px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Brand: logo Forjamente + wordmark mono. Pequeno e discreto pra não
              roubar atenção do conteúdo. Logo herda do SVG oficial em /icons. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
            <img src="/icons/forjamente-icon.png" alt="" width={42} height={42}
              style={{ borderRadius: 10, flexShrink: 0 }}/>
            <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div className="mono" style={{
                fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase',
                color: 'var(--ink-2)', fontWeight: 600, lineHeight: 1,
              }}>Forjamente</div>
              <div style={{
                fontSize: 9.5, letterSpacing: '0.12em', textTransform: 'uppercase',
                color: 'var(--ink-3)', lineHeight: 1,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {dateStr}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => openExternalUrl('https://www.instagram.com/forjamente.1')} aria-label="Instagram @forjamente.1" style={{
              width: 38, height: 38, borderRadius: '50%',
              background: 'var(--paper-2)', border: '1px solid var(--line)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="var(--ink-2)" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="5.4"/>
                <circle cx="12" cy="12" r="4"/>
                <circle cx="17.4" cy="6.6" r="1.1" fill="var(--ink-2)" stroke="none"/>
              </svg>
            </button>
            {onOpenSupport && (
              <button onClick={onOpenSupport} aria-label="Suporte" style={{
                width: 38, height: 38, borderRadius: '50%',
                background: 'var(--paper-2)', border: '1px solid var(--line)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon name="help" size={18} />
              </button>
            )}
            <button data-notif-anchor onClick={onOpenNotif} aria-label={notifUnreadCount > 0 ? `Notificações (${notifUnreadCount} novas)` : 'Notificações'} style={{
              width: 38, height: 38, borderRadius: '50%',
              background: 'var(--paper-2)', border: '1px solid var(--line)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              position: 'relative',
            }}>
              <Icon name="bell" size={18} />
              {notifUnreadCount > 0 && (
                <span style={{
                  position: 'absolute', top: -2, right: -2,
                  minWidth: 18, height: 18, padding: '0 5px',
                  borderRadius: 999,
                  background: 'var(--ember)', color: 'var(--paper)',
                  fontSize: 10, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '2px solid var(--paper)',
                  fontFamily: 'Geist Mono, monospace',
                  letterSpacing: '0.02em',
                  boxShadow: '0 4px 8px -2px rgba(199,83,26,0.45)',
                }}>
                  {notifUnreadCount > 9 ? '9+' : notifUnreadCount}
                </span>
              )}
            </button>
          </div>
        </div>
        <div className="serif" style={{ fontSize: 30, lineHeight: 1.1, marginTop: 16, letterSpacing: '-0.01em' }}>
          {greeting},{' '}
          <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>{displayName}</span>.
        </div>
        {meuCirculo && <MeuCirculoCartao session={session} onAbrir={onAbrirMeuCirculo!}/>}
      </div>

      {/* Cor literal ESCURA nos rótulos do card de dia vencido: o fundo dele é
          var(--ink), que no tema midnight é o CREME. Antes eles usavam creme
          translúcido — creme sobre creme, invisível. Bug de produção que só
          apareceu quando este staging voltou a servir a Home antiga. */}
      {/* O herói da Home. Com programa ativo, quem manda é o estado do dia —
          um organismo só, com um caminho só. Sem programa, o anel de metas
          antigo continua servindo quem só quer acompanhar leituras. */}
      {mostraProtocolo && habito ? (
        <HeroProtocolo
          estado={habito}
          diasComCheckin={(habito.diasComCheckin && habito.diasComCheckin.length)
            ? habito.diasComCheckin
            : diasLidosNoApp}
          hoje={today}
          livroAtual={livroDoCard}
          sequenciaAlternativa={{
            atual: currentStreak,
            melhor: streak?.longest || 0,
            diasLidos: diasLidosNoApp.length,
          }}
          leuHojeAlternativo={dayPagesRead > 0 || dayDone}
          paginasHojeAlternativo={dayPagesRead}
          // O botão do card abre o MODAL DE REGISTRO DE SEMPRE, já no modo foco
          // com o cronômetro rodando — e não a tela de sessão própria do
          // protocolo, que era um segundo cronômetro para a mesma coisa. O
          // check-in do dia continua idêntico: registrar por aqui recarrega o
          // estado do hábito e dispara a celebração do protocolo (App.tsx).
          onAbrirLivro={() => {
            // Livro ainda não começado abre em "De onde parei": o cronômetro na
            // frente, com 0 página, era o resto do UX-03 (pente fino, UX-19).
            if (livroDoCard) return onRead(livroDoCard, (livroDoCard.read || 0) > 0 ? 'foco' : 'linear');
            // Sem livro na estante o botão apontava para a sessão do protocolo,
            // que exige um livro do programa: quem não tem nenhum tocava num
            // botão morto. Aqui o passo seguinte é escolher um livro.
            if (habito.livro && onAbrirLivroDoProtocolo) return onAbrirLivroDoProtocolo();
            if (onAddBook) return onAddBook();
          }}
          onComecarProtocolo={() => onComecarProtocolo && onComecarProtocolo()}
          onVerPrograma={() => onVerPrograma && onVerPrograma()}
          onTrocarLivro={onTrocarLivro}
          onVerLivro={onVerLivro}
          ofereceTroca={ofereceTroca}
        />
      ) : (
      <div style={{ padding: '24px 20px 0' }}>
        <div style={{
          background: dayDone ? 'var(--ink)' : 'var(--paper-2)',
          color: dayDone ? 'var(--paper)' : 'var(--ink)',
          border: dayDone ? '1px solid var(--ember)' : '1px solid var(--line)',
          borderRadius: 26, padding: 22,
          position: 'relative', overflow: 'hidden',
          transition: 'background 600ms, color 600ms',
          boxShadow: dayDone ? '0 20px 40px -12px rgba(199,83,26,0.35)' : 'none',
        }}>
          <svg width="420" height="260" style={{ position: 'absolute', top: -40, right: -120, opacity: 0.06 }}>
            <circle cx="210" cy="130" r="180" fill="none" stroke="var(--ember)" strokeWidth="40"/>
          </svg>

          {/* Adesivo para Stories — caminho NOVO, montado em canvas. Convive de
              propósito com o ShareButton de print do DOM que existe nos outros
              cards: são duas abordagens diferentes, pra poder comparar. */}
          {!isFirstTime && <BotaoOverlayStory onClick={() => setOverlayAberto(true)}/>}

          {/* Onda 4: toggle de visão por unidade — só aparece se o usuário tem
              livros em mais de uma unidade. Nunca soma unidades diferentes. */}
          {unitsPresent.length > 1 && !isFirstTime && onGoalViewUnit && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 5, marginBottom: 12, position: 'relative', zIndex: 3 }}>
              {unitsPresent.map((uu) => {
                const on = goalViewUnit === uu;
                return (
                  <button key={uu} onClick={() => onGoalViewUnit(uu as any)} style={{
                    padding: '5px 11px', borderRadius: 999, fontSize: 11.5, fontWeight: 600,
                    fontFamily: 'Geist Mono, monospace', letterSpacing: '0.02em', cursor: 'pointer', border: 'none',
                    background: on ? 'var(--ember)' : (dayDone ? 'rgba(255,255,255,0.14)' : 'var(--paper-3)'),
                    color: on ? '#1a1208' : (dayDone ? 'var(--paper)' : 'var(--ink-3)'),
                  }}>{unitMeta(uu).capital}</button>
                );
              })}
            </div>
          )}

          {/* Row do anel + texto. Quando expandido, o anel migra pro centro
              via position:absolute animado — o spacer mantém o layout estável
              e o texto da direita só faz fade-out (não muda de tamanho). */}
          <div style={{
            position: 'relative',
            // Altura sobe pra acomodar o scale 1.4 (anel visual ~151px) com
            // pequena folga vertical pra não encostar no border do card.
            minHeight: ringExpanded && hasMultipleBooks ? 160 : 108,
            transition: 'min-height 450ms cubic-bezier(.2,.8,.2,1)',
          }} data-tour="today-ring">
            <div style={{
              display: 'flex', gap: 18, alignItems: 'center', height: '100%',
            }}>
              {/* Spacer 108x108 — preserva a posição original do texto da direita
                  enquanto o anel real, posicionado absolute logo abaixo, migra
                  pro centro do card. */}
              <div style={{ width: 108, height: 108, flexShrink: 0 }}/>
              <div style={{
                flex: 1,
                opacity: ringExpanded && hasMultipleBooks ? 0 : 1,
                transition: 'opacity 250ms ease',
                pointerEvents: ringExpanded && hasMultipleBooks ? 'none' : 'auto',
              }}>
                <div style={{ fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', color: dayDone ? 'var(--ember)' : 'var(--ink-3)' }}>
                  {isFirstTime ? 'Primeiro passo' : dayDone ? 'O dia venceu' : 'Faltam'}
                </div>
              <div className="serif" style={{ fontSize: isFirstTime ? 22 : 28, lineHeight: 1.15, marginTop: 4 }}>
                {isFirstTime ? <>
                  Adicione seu<br/>
                  <span style={{ fontStyle: 'italic', color: 'var(--ember)' }}>primeiro livro</span>.
                </> : dayDone ? <span style={{ fontStyle: 'italic' }}>Hábito<br/>cumprido.</span> : <>
                  {Math.max(0, dayTotalGoal - dayPagesRead)} {gu.long}<br/>
                  <span style={{ fontStyle: 'italic', color: 'var(--ink-3)', fontSize: 20 }}>para o hábito.</span>
                </>}
              </div>
              {isFirstTime && onAddBook && (
                <button onClick={onAddBook} style={{
                  marginTop: 12, padding: '10px 16px', borderRadius: 12,
                  background: 'var(--ember)', color: 'var(--paper)',
                  fontSize: 13, fontWeight: 500,
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}>
                  <Icon name="plus" size={14} color="var(--paper)" stroke={2.2}/>
                  Adicionar livro
                </button>
              )}
              </div>
            </div>

            {/* Anel real — posição absolute, animando entre canto esquerdo e
                centro horizontal do card. O scale fica isolado dentro do SVG via
                prop `scale`, então o text overlay (HOJE / 18 / de 60 pág) não
                cresce nem distorce. */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: ringExpanded && hasMultipleBooks ? '50%' : '0',
              transform: ringExpanded && hasMultipleBooks
                ? 'translate(-50%, -50%)'
                : 'translate(0, -50%)',
              transition: 'left 450ms cubic-bezier(.2,.8,.2,1), transform 450ms cubic-bezier(.2,.8,.2,1)',
              zIndex: 2,
            }}>
              <ProgressRing
                value={isFirstTime ? 0 : todayPct}
                segments={ringExpanded && hasMultipleBooks ? ringSegments.segments : undefined}
                size={108} stroke={8}
                scale={ringExpanded && hasMultipleBooks ? 1.4 : 1}
                spinTurns={spinTurns}
                color="var(--ember)"
                trackColor={dayDone ? 'rgba(22,18,16,0.10)' : 'var(--paper-3)'}
                onClick={hasMultipleBooks ? toggleRing : undefined}>
                <div className="mono" style={{ fontSize: 10, color: dayDone ? 'var(--ember)' : 'var(--ink-3)', letterSpacing: '0.1em' }}>
                  {isFirstTime ? 'COMECE' : dayDone ? 'COMPLETO' : 'HOJE'}
                </div>
                <div className="serif" style={{ fontSize: 38, lineHeight: 1 }}>
                  {isFirstTime ? '0' : dayPagesRead}
                </div>
                <div className="mono" style={{ fontSize: 10, color: dayDone ? 'rgba(22,18,16,0.55)' : 'var(--ink-3)' }}>
                  {isFirstTime ? gu.short : dayDone ? '✓ meta batida' : `de ${dayTotalGoal} ${gu.short}`}
                </div>
              </ProgressRing>
            </div>
          </div>

          {/* Legenda do anel — aparece quando expandido + tem 2+ livros lidos hoje.
              Slide-down suave; lista compacta com bullet colorido + título + páginas. */}
          <div style={{
            maxHeight: ringExpanded && hasMultipleBooks ? 200 : 0,
            opacity: ringExpanded && hasMultipleBooks ? 1 : 0,
            overflow: 'hidden',
            transition: 'max-height 400ms cubic-bezier(.2,.8,.2,1), opacity 250ms ease, margin-top 350ms',
            marginTop: ringExpanded && hasMultipleBooks ? 14 : 0,
          }}>
            <div className="mono" style={{
              fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase',
              color: dayDone ? 'rgba(22,18,16,0.55)' : 'var(--ink-3)',
              marginBottom: 8,
            }}>
              composição
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {ringSegments.list.map((it, i) => (
                <div key={it.book.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  fontSize: 12,
                  color: dayDone ? 'rgba(22,18,16,0.80)' : 'var(--ink-2)',
                  // Stagger sutil — cada linha aparece com 60ms de atraso
                  transitionDelay: `${100 + i * 60}ms`,
                }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: it.color,
                    flexShrink: 0,
                  }}/>
                  <span style={{
                    flex: 1, minWidth: 0,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{it.book.title}</span>
                  <span className="mono" style={{
                    fontSize: 10, letterSpacing: '0.06em',
                    color: dayDone ? 'rgba(22,18,16,0.55)' : 'var(--ink-3)',
                    flexShrink: 0,
                  }}>{it.pagesToday} {gu.short}</span>
                </div>
              ))}
            </div>
          </div>

          {/* O LIVRO E A AÇÃO, dentro do card principal.
              Antes este card era só placar: anel, "faltam N páginas" e a chama.
              Pra registrar leitura era preciso ROLAR até "Continue onde parou",
              achar o livro e só então tocar — e ainda escolher o modo. O card do
              Protocolo já provava o contrário: livro e botão na mesma moldura,
              um toque. É esse pedaço que vem pra cá.
              O livro é o do ÚLTIMO REGISTRO, então o card segue quem lê. */}
          {!isFirstTime && livroDoCard && (
            <div style={{
              marginTop: 18, paddingTop: 16,
              borderTop: dayDone ? '1px solid rgba(22,18,16,0.14)' : '1px solid var(--line)',
            }}>
              <button
                onClick={() => onOpenBook(livroDoCard)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, width: '100%',
                  textAlign: 'left', background: 'transparent', border: 'none',
                  padding: 0, cursor: 'pointer', marginBottom: 12,
                }}
              >
                <BookCover book={livroDoCard} width={40} height={58}/>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 15, fontWeight: 600, lineHeight: 1.2,
                    color: dayDone ? 'rgba(22,18,16,0.92)' : 'var(--ink)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{livroDoCard.title}</div>
                  <div style={{ fontSize: 12.5, marginTop: 3, color: dayDone ? 'rgba(22,18,16,0.55)' : 'var(--ink-3)' }}>
                    {livroDoCard.totalPages
                      ? `${livroDoCard.read || 0} de ${livroDoCard.totalPages} ${gu.short}`
                      : `${livroDoCard.read || 0} ${gu.short}`}
                  </div>
                </div>
              </button>

              <button onClick={() => onRead(livroDoCard)} style={{
                width: '100%', padding: '14px 18px', borderRadius: 999, border: 'none',
                background: dayDone ? 'rgba(22,18,16,0.92)' : 'var(--ember)',
                color: dayDone ? 'var(--paper)' : '#1c1208',
                fontSize: 15, fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}>
                <Icon name="timer" size={17} color={dayDone ? 'var(--paper)' : '#1c1208'} stroke={2}/>
                Registrar leitura
              </button>
              <div style={{ fontSize: 12, textAlign: 'center', marginTop: 8, color: dayDone ? 'rgba(22,18,16,0.5)' : 'var(--ink-4)' }}>
                Abre no modo foco. Uma página já vence o dia.
              </div>
            </div>
          )}

          <div data-tour="streak" style={{
            marginTop: 20, paddingTop: 18,
            borderTop: dayDone ? '1px solid rgba(22,18,16,0.14)' : '1px solid var(--line)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Flame count={currentStreak} size={28}/>
              <div>
                {/* Invariante 4 — nunca mostrar zero. "0 dias acesa" comunica que
                    tudo foi perdido; o que ficou é a melhor marca. */}
                <div style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: dayDone ? 'rgba(22,18,16,0.55)' : 'var(--ink-3)' }}>Chama</div>
                <div style={{ fontSize: 15, fontWeight: 500 }}>
                  {currentStreak > 0
                    ? `${currentStreak} ${currentStreak === 1 ? 'dia aceso' : 'dias acesa'}`
                    : (streak?.longest || 0) > 0
                      ? `melhor marca · ${streak?.longest} ${streak?.longest === 1 ? 'dia' : 'dias'}`
                      : 'acenda hoje'}
                </div>
              </div>
            </div>
            <StreakTrail history={(streak?.history || []).slice(-30)} todayState={todayState}/>
          </div>
        </div>
      </div>
      )}


      {/* Quem dispensou o programa continua com o app inteiro — e ganha uma
          porta, não uma cutucada. Prometi não perguntar de novo, então isto é
          uma linha parada na tela, nunca um pop-up. */}
      {/* A porta de entrada do protocolo para quem não está nele. Com a chave na
          tela esta linha nunca aparecia; e quem estava "explorando" tinha um
          card próprio, que saiu na fusão. Agora ela atende os dois — e qualquer
          um sem programa —, sempre como linha parada, nunca como pop-up. */}
      {!!habito && ['DISPENSADO', 'EXPLORANDO', 'LEITOR'].includes(habito.estado)
        && habito.inscricao?.status !== 'ativo' && onComecarProtocolo && (
        <div style={{ padding: '18px 20px 0' }}>
          <button onClick={onComecarProtocolo} style={{
            width: '100%', textAlign: 'left', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 11,
            padding: '13px 15px', borderRadius: 16,
            border: '1px dashed var(--line-strong)', background: 'transparent',
          }}>
            <Icon name="flame" size={16} color="var(--ink-3)" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.45 }}>
                O Protocolo do Hábito continua aqui, quando você quiser.
              </div>
            </div>
            <Icon name="chevron-right" size={14} color="var(--ink-4)" />
          </button>
        </div>
      )}

      {/* O marco do miolo vem ANTES do diagnóstico: reconhecimento de algo
          conquistado tem que chegar antes da régua que mede o que falta. */}
      {mostraProtocolo && habito?.marco && onMarcoVisto && (
        <MarcoDoMiolo
          chave={habito.marco.chave}
          dados={habito.marco.dados}
          onFechar={() => onMarcoVisto(habito.marco!.chave)}
        />
      )}

      {/* "2 dias lidos" não informa nada sozinho. Este card responde as três
          perguntas que vêm logo depois de olhar o número: estou indo bem, o que
          isso significa, e o que acontece se eu continuar assim. */}
      {mostraProtocolo && habito?.diagnostico && (
        <ComoVoceEstaIndo
          estado={habito}
          onVerPrograma={onVerPrograma}
          onAbrirContrato={onEditarContrato}
        />
      )}

      {/* Stories SAÍRAM da Home. A feature continua viva (clubes, perfil) — o que
          mudou é que ela deixou de ocupar o segundo lugar da tela inicial. Aqui
          o foco é o hábito: o que vem depois do herói tem que ser o SEU
          progresso, não o dos outros. Trocar `false` por `session` devolve. */}
      {false && session && (
        <StoriesProvider home session={session} clubId={null} isElevated={false} onGoToClub={onOpenClub as any} onOpenProfile={onOpenProfile as any} viewer={{ id: (profile as any)?.id, name: (profile as any)?.display_name || (profile as any)?.username || 'Você', username: (profile as any)?.username, avatarUrl: (profile as any)?.avatar_url }}>
          <div style={{ marginTop: 14 }}><StoriesTray/></div>
          <StoriesOverlays/>
        </StoriesProvider>
      )}

      {personalActive.length === 0 ? (
        <>
          <div className="section-head">
            <div className="serif title">Continue onde parou</div>
          </div>
          <div style={{ padding: '0 20px' }}>
            {clubsWithBooks.length === 0 ? (
              <div style={{
                padding: 28, borderRadius: 22, background: 'var(--paper-2)',
                border: '1px dashed var(--line-strong)', textAlign: 'center',
              }}>
                <div className="serif" style={{ fontSize: 22, lineHeight: 1.2 }}>Nenhum livro aberto</div>
                <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 6, fontStyle: 'italic' }}>
                  O hábito começa com o primeiro.
                </div>
                {onAddBook && (
                  <button onClick={onAddBook} style={{
                    marginTop: 18, padding: '14px 22px', borderRadius: 14,
                    background: 'var(--ember)', color: 'var(--paper)',
                    fontSize: 14, fontWeight: 500,
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                  }}>
                    <Icon name="plus" size={15} color="var(--paper)" stroke={2.2}/>
                    Adicionar primeiro livro
                  </button>
                )}
              </div>
            ) : (
              <div style={{
                padding: 16, borderRadius: 18, background: 'var(--paper-2)',
                border: '1px dashed var(--line-strong)', textAlign: 'center',
              }}>
                <div style={{ fontSize: 13, color: 'var(--ink-3)', fontStyle: 'italic' }}>
                  Nenhum livro pessoal — adicione um pelo botão central <span style={{ fontSize: 14 }}>＋</span>.
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <CarouselSection count={personalActive.length}
          headerLeft={<div className="serif title">Continue onde parou</div>}
          headerRight={<button className="label" onClick={onOpenLibrary} style={{ background: 'none' }}>Ver tudo</button>}>
          {[...personalActive].sort((a, b) => bookHeat(a, today).days - bookHeat(b, today).days).map((b, i) => {
            const locked = !isPro && !unlockedReadingIds.has(b.id);
            const card = (
              <ContinueCard book={b}
                session={session}
                mostrarMeta={!mostraProtocolo}
                onOpen={() => onOpenBook(b)}
                onRead={() => onRead(b)}
                onStudy={onStudy ? () => onStudy(b) : undefined}
                novasMentor={mentorNovas?.[b.id] || 0}
                onTravessia={onTravessia && travessias?.[b.id] ? () => onTravessia(b) : undefined}
                onActions={onBookActions ? () => onBookActions(b) : undefined}
                bookDone={!!dailyDoneFor[`${today}:${b.id}`]}
                today={today}/>
            );
            return (
              <div key={b.id} data-tour={i === 0 ? 'continue-card' : undefined}>
                {locked ? (
                  <Lockable locked onUnlock={onOpenPaywall} label="Leituras simultâneas são Pro">{card}</Lockable>
                ) : card}
              </div>
            );
          })}
        </CarouselSection>
      )}

      {/* Leitura em grupo vem DEPOIS da individual. O protocolo é sobre um
          hábito seu, com um livro seu — o clube é apoio, não o eixo. Enquanto o
          hábito não está formado, o que precisa estar ao alcance do polegar é o
          seu livro. */}
      {clubsWithBooks.length > 0 && (
        <>
          <CarouselSection count={clubsWithBooks.length} headerLeft={
            <div>
              <div className="label">{clubsTerm === 'grupo' ? 'Grupos' : 'Clubes'} em que você está</div>
              <div className="serif title" style={{ marginTop: 4 }}>Lendo com gente</div>
            </div>
          }>
            {[...clubsWithBooks].sort((a: any, b: any) => clubHeatDays(a, books, today) - clubHeatDays(b, books, today)).map((c: any) => {
              // Espelho pessoal do livro de clube — fonte das sessões e dailyGoal do user.
              const mirror = books.find(b => (b as any).fromClubId === c.id) || null;
              return (
                <ClubBookCard key={c.id} club={c} term={clubsTerm}
                  today={today}
                  personalMirror={mirror}
                  onOpenProfile={onOpenProfile}
                  onOpen={() => onOpenClub && onOpenClub(c.id)}
                  onActions={onClubActions ? () => onClubActions(c, mirror) : undefined}
                  onRead={onClubBookRead ? () => onClubBookRead(c.id) : undefined}/>
              );
            })}
          </CarouselSection>
        </>
      )}

      {/* Círculo do Mentor (QA #1): a entrada no app para a página de quem tem
          série publicada. A lista vem da API; sem Mentor, nada aparece. */}
      {onAbrirCirculo && <CirculosDosMentores session={session} onAbrir={onAbrirCirculo} semOMeu={meuCirculo}/>}

      {/* HM4 — Linha de convite "Leitura é melhor junto" (no lugar do antigo card
          do Instagram, que virou ícone no topo). */}
      <div style={{ padding: '20px 20px 0' }}>
        <button onClick={inviteFriends} style={{
          width: '100%', textAlign: 'left', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 13,
          padding: '13px 15px', borderRadius: 18,
          border: '1px solid var(--line)',
          backgroundImage: 'linear-gradient(100deg, var(--ember-soft), var(--paper-2))',
        }}>
          <div style={{ display: 'flex', flexShrink: 0 }}>
            <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--paper-3)', border: '2px solid var(--paper)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="users" size={14} color="var(--ember)" stroke={2}/>
            </div>
            <div style={{ width: 30, height: 30, borderRadius: '50%', marginLeft: -11, background: 'var(--paper-2)', border: '2px dashed var(--ember)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ember)', fontSize: 16, fontWeight: 600, lineHeight: 1 }}>+</div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="serif" style={{ fontSize: 15, lineHeight: 1.15, color: 'var(--ink)' }}>Leitura é melhor junto.</div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2 }}>Chame seus amigos pra ler com você.</div>
          </div>
          <span className="mono" style={{ flexShrink: 0, fontSize: 11, color: 'var(--ember)', border: '1px solid var(--ember)', borderRadius: 999, padding: '8px 13px' }}>Convidar</span>
        </button>
      </div>

      {/* A cadência semanal só aparece para quem NÃO está no protocolo. Para
          quem está, ela repetiria a semana que o herói já desenha, com outra
          régua (soma de metas em vez de frequência) — dois placares do mesmo dia
          se contradizendo. Para quem não está, ela não é redundante com nada, e
          tirá-la seria degradar o app de quem não pediu o programa. */}
      {!mostraProtocolo && (<>
      <div className="section-head">
        <div>
          <div className="label">Esta semana</div>
          <div className="serif title" style={{ marginTop: 4 }}>Sua cadência</div>
        </div>
        {/* Share da cadência semanal */}
        <ShareButton type="reading" variant="icon-soft" size={16} />
      </div>

      <div style={{ padding: '0 20px' }}>
        <div className="card" data-share-target="card" data-share-bg="#161210">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div>
              <div className="serif" style={{ fontSize: 36, lineHeight: 1 }}>
                {weekDone}<span style={{ fontSize: 18, color: 'var(--ink-3)' }}> / {weekGoal}</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                {isFirstTime
                  ? 'Quando você começar, seu progresso semanal aparece aqui.'
                  : goalViewUnit === 'pages' ? 'páginas lidas esta semana' : `${gu.long} esta semana`}
              </div>
            </div>
            {weekGoal > 0 && !isFirstTime && (
              <div className="mono" style={{
                fontSize: 11, color: 'var(--ember)', padding: '4px 8px',
                background: 'var(--ember-soft)', borderRadius: 6, letterSpacing: '0.08em',
              }}>{Math.round((weekDone/weekGoal)*100)}%</div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 6, marginTop: 18, justifyContent: 'space-between' }}>
            {last7.map((dayData: any, i: number) => {
              const read = !!dayData && dayData.read > 0;
              const isToday = i === last7.length - 1;
              // Letra do dia derivada da DATA real (antes era um array fixo
              // S-T-Q-Q-S-S-D que só batia certo aos domingos).
              const wd = dayData?.date ? new Date(dayData.date + 'T12:00:00').getDay() : -1;
              const d = wd >= 0 ? (['D','S','T','Q','Q','S','S'][wd]) : '·';
              return (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%',
                    background: read ? 'var(--ember)' : 'var(--paper-3)',
                    border: isToday && !read ? '2px dashed var(--ember)' : 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {read && <Icon name="check" size={16} color="var(--paper)" stroke={2.5}/>}
                  </div>
                  <div style={{ fontSize: 10, color: isToday ? 'var(--ember)' : 'var(--ink-4)', fontWeight: isToday ? 600 : 400 }}>{d}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      </>)}

      {/* Quote card */}
      <div style={{ padding: '24px 20px 0' }}>
        <div data-share-target="card" data-share-bg="#F3ECDE" style={{
          background: 'var(--ink)', color: 'var(--paper)',
          borderRadius: 22, padding: '28px 22px',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', top: 8, left: 18,
            fontFamily: 'Instrument Serif, serif',
            fontSize: 120, lineHeight: 1, color: 'var(--ember)',
            opacity: 0.35,
          }}>"</div>
          <div className="serif" style={{ fontSize: 22, lineHeight: 1.25, position: 'relative', fontStyle: 'italic', marginTop: 18 }}>
            {quote.text}
          </div>
          <div className="mono" style={{ fontSize: 11, marginTop: 16, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-4)' }}>
            — {quote.author}
          </div>
          {dailyQuote?.book?.title && (
            <div className="mono" style={{
              fontSize: 9, marginTop: 6, letterSpacing: '0.18em', textTransform: 'uppercase',
              color: 'var(--ember)', opacity: 0.75,
            }}>
              inspirado em · {dailyQuote.book.title}
            </div>
          )}
          {/* Share button inline no rodapé — sem absolute, integrado ao flow */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
            <ShareButton type="quote" variant="icon-soft" size={16} title="Compartilhar citação" />
          </div>
        </div>
      </div>

      <div style={{ padding: '24px 20px 0', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
        {[
          { label: 'Livros', value: totals.books, hint: 'terminados' },
          { label: 'Páginas', value: totals.pages.toLocaleString('pt-BR'), hint: 'no total' },
          { label: 'Horas', value: totals.hours, hint: 'lendo' },
        ].map((s, i) => (
          <div key={i} style={{
            background: 'var(--paper-2)', borderRadius: 16, padding: '14px 12px',
            border: '1px solid var(--line)',
          }}>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--ink-3)' }}>{s.label}</div>
            <div className="serif" style={{ fontSize: 22, lineHeight: 1.1, marginTop: 6 }}>{s.value}</div>
            <div style={{ fontSize: 10, color: 'var(--ink-4)', marginTop: 2 }}>{s.hint}</div>
          </div>
        ))}
      </div>

      {/* Fora do card de propósito: o card tem overflow:hidden e o modal é
          fixo em tela cheia. */}
      <OverlayStoryModal
        aberto={overlayAberto}
        onFechar={() => setOverlayAberto(false)}
        dados={{
          tipo: 'meta',
          paginasHoje: dayPagesRead,
          metaHoje: dayTotalGoal,
          unidadeLonga: gu.long,
          chama: currentStreak,
          metaBatida: dayDone,
        }}
      />
    </div>
  );
};

// ---- Calor da leitura (HM2): Em dia / Esfriando / Parado --------------------
// Base: dias desde a última sessão de leitura (book.sessions). Sem sessões, usa a
// data de adição (livro novo não nasce "parado"). Limiares: esfria após 24h (1d),
// para após 72h (3d). Datas em SP date-key 'YYYY-MM-DD'.
const HEAT_COOL_DAYS = 1;
const HEAT_STALL_DAYS = 3;
type HeatState = 'hot' | 'warm' | 'cold';
function daysSinceKey(today: string, key?: string | null): number {
  if (!key || !today) return 0;
  const a = Date.parse((today.length > 10 ? today.slice(0, 10) : today) + 'T00:00:00');
  const b = Date.parse((String(key).length > 10 ? String(key).slice(0, 10) : String(key)) + 'T00:00:00');
  if (isNaN(a) || isNaN(b)) return 0;
  return Math.max(0, Math.round((a - b) / 86400000));
}
function bookHeat(book: BookV2 | null | undefined, today: string): { state: HeatState; days: number } {
  const sessions = (book?.sessions || []).filter(s => (s.pages || 0) > 0);
  let last: string | null = null;
  if (sessions.length) last = sessions.reduce((m, s) => (s.date > m ? s.date : m), sessions[0].date);
  else { const added = (book as any)?.addedDate || (book as any)?.createdAt || null; last = added ? String(added).slice(0, 10) : null; }
  const days = daysSinceKey(today, last);
  const state: HeatState = days < HEAT_COOL_DAYS ? 'hot' : (days < HEAT_STALL_DAYS ? 'warm' : 'cold');
  return { state, days };
}
// Dias de "esfriamento" de um livro de clube (via espelho pessoal). Sem espelho
// (membro ainda não registrou leitura) → vai pro fim da ordem.
function clubHeatDays(club: any, books: BookV2[], today: string): number {
  const m = books.find(bk => (bk as any).fromClubId === club.id) || null;
  return m ? bookHeat(m, today).days : 999;
}
const HEAT_UI: Record<HeatState, { color: string; bg: string; border: string; opacity: number; filter: string; bar: string }> = {
  hot:  { color: '#E5853F', bg: 'rgba(229,113,58,0.12)',  border: 'rgba(229,113,58,0.28)',  opacity: 1,    filter: 'none',                              bar: 'var(--ember)' },
  warm: { color: '#D9A24A', bg: 'rgba(217,162,74,0.12)',  border: 'rgba(217,162,74,0.28)',  opacity: 0.9,  filter: 'saturate(0.9)',                     bar: '#C9A24A' },
  cold: { color: '#8FA3C0', bg: 'rgba(143,163,192,0.12)', border: 'rgba(143,163,192,0.28)', opacity: 0.72, filter: 'grayscale(0.45) brightness(0.88)', bar: '#6E7681' },
};
function heatLabel(h: { state: HeatState; days: number }): string {
  return h.state === 'hot' ? 'Em dia' : (h.state === 'warm' ? `Esfriando · ${h.days}d` : `Parado · ${h.days}d`);
}
function HeatIcon({ state, color }: { state: HeatState; color: string }) {
  if (state === 'hot') return <svg width="11" height="11" viewBox="0 0 24 24" fill={color}><path d="M12 2c1.2 3-2 4.2-2 7.2a2 2 0 0 0 4 0c1.8 1.8 3 3 3 6a5 5 0 0 1-10 0c0-4.2 4-5.5 5-13.2Z"/></svg>;
  if (state === 'warm') return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.9}><path d="M14 14.8V5a2 2 0 1 0-4 0v9.8a4 4 0 1 0 4 0Z"/><path d="M12 9v6" strokeLinecap="round"/></svg>;
  return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.9} strokeLinecap="round"><path d="M12 3v18M4 7.5l16 9M20 7.5l-16 9"/></svg>;
}
function HeatChip({ heat }: { heat: { state: HeatState; days: number } }) {
  const ui = HEAT_UI[heat.state];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 9px', borderRadius: 8,
      background: ui.bg, border: `1px solid ${ui.border}`,
      fontFamily: 'Geist Mono, monospace', fontSize: 9.5, letterSpacing: '0.02em', color: ui.color, whiteSpace: 'nowrap',
    }}>
      <HeatIcon state={heat.state} color={ui.color} />
      {heatLabel(heat)}
    </span>
  );
}

// Carrossel horizontal com scroll-snap + bolinhas de posição. Mostra 1 card por
// vez (com leve "peek" do próximo) — economiza espaço e dá fluidez no Home quando
// há vários livros/clubes. 0 itens → null; 1 item → full-width sem carrossel/bolinhas.
// Carrossel de seção: header com título à esquerda + bolinhas de posição à direita;
// abaixo, 1 card por tela (full-width, próximo escondido), scroll-snap + leve parallax
// de escala na troca. 0 itens → null; 1 item → sem bolinhas.
function CarouselSection({ count, headerLeft, headerRight, children }: {
  count: number;
  headerLeft: React.ReactNode;
  headerRight?: React.ReactNode;
  children: React.ReactNode;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [active, setActive] = React.useState(0);
  const activeRef = React.useRef(0);
  const applyFx = React.useCallback(() => {
    const el = ref.current; if (!el) return;
    const arts = Array.from(el.children) as HTMLElement[];
    if (!arts.length) return;
    const w = arts[0].offsetWidth, step = w + 16;
    const vpCenter = el.scrollLeft + el.clientWidth / 2;
    arts.forEach((a, i) => {
      let t = (i * step + w / 2 - vpCenter) / step;
      t = Math.max(-1, Math.min(1, t));
      a.style.transform = `scale(${(1 - 0.05 * Math.abs(t)).toFixed(3)})`;
    });
    const idx = Math.max(0, Math.min(count - 1, Math.round(el.scrollLeft / step)));
    if (idx !== activeRef.current) { activeRef.current = idx; setActive(idx); }
  }, [count]);
  React.useEffect(() => { const id = setTimeout(applyFx, 30); return () => clearTimeout(id); }, [applyFx, count]);
  if (count <= 0) return null;
  const dots = count > 1 ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      {Array.from({ length: count }).map((_, i) => (
        <span key={i} onClick={() => {
          const el = ref.current; const a = el?.firstElementChild as HTMLElement | null;
          if (el && a) el.scrollTo({ left: i * (a.offsetWidth + 16), behavior: 'smooth' });
        }} style={{
          height: 6, width: i === active ? 18 : 6, borderRadius: 3,
          background: i === active ? 'var(--ember)' : 'var(--line-strong)',
          transition: 'width .2s, background .2s', cursor: 'pointer',
        }} />
      ))}
    </div>
  ) : null;
  const header = (
    <div className="section-head">
      {headerLeft}
      {(headerRight || dots) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>{headerRight}{dots}</div>
      )}
    </div>
  );
  if (count === 1) return (<>{header}<div style={{ padding: '0 20px' }}>{children}</div></>);
  return (
    <>
      {header}
      <div ref={ref} onScroll={applyFx} style={{
        display: 'flex', gap: 16, overflowX: 'auto',
        scrollSnapType: 'x mandatory', padding: '4px 20px 8px',
        WebkitOverflowScrolling: 'touch',
      }}>
        {React.Children.map(children, (child) => (
          <div style={{ flex: '0 0 100%', minWidth: 0, scrollSnapAlign: 'center' }}>{child}</div>
        ))}
      </div>
    </>
  );
}

// "ANTERIORMENTE EM…" (pente fino, UX-34). O caderno é onde a leitura vira
// memória — mas a nota do dia 1 não voltava no dia 2. A faixa traz a última
// nota do livro (uma linha, com a página) e o toque abre o caderno, onde ela
// é a primeira. Só leitura; sem nota, nada aparece.
function primeiraLinha(n: any): string {
  const bruto = String(n?.title || n?.text || '').split('\n').map((l) => l.replace(/^[#>*\-\s]+/, '').replace(/\*\*|__|==/g, '').trim()).find(Boolean) || '';
  if (bruto) return bruto;
  if (n?.audio) return 'Nota em áudio';
  if (n?.photo) return 'Foto de página';
  return '';
}
// "Meu Círculo": a entrada do Mentor no editor, logo abaixo da saudação. Sem
// legenda — o nome da série atual (o rascunho conta) ou o primeiro passo.
function MeuCirculoCartao({ session, onAbrir }: { session: any; onAbrir: () => void }) {
  const [meu, setMeu] = useState<any>(undefined);
  useEffect(() => {
    if (!session) return;
    let vivo = true;
    api.getMeuCirculo(session).then((r) => { if (vivo) setMeu(r?.mentor || null); }).catch(() => { if (vivo) setMeu(null); });
    return () => { vivo = false; };
  }, [session?.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!meu) return null;
  return (
    <button data-home-meu-circulo="" onClick={onAbrir} style={{
      width: '100%', marginTop: 16, textAlign: 'left', cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '11px 14px', borderRadius: 16,
      border: '1px solid var(--ember)', background: 'var(--paper-2)',
    }}>
      <Icon name="users" size={18} color="var(--ember)" stroke={2}/>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="serif" style={{ fontSize: 16, lineHeight: 1.15, color: 'var(--ink)' }}>Meu Círculo</div>
        <div data-meu-circulo-serie="" style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {meu.serie?.titulo || 'Comece sua primeira série'}
        </div>
      </div>
      <Icon name="chevron-right" size={16} color="var(--ink-3)" stroke={2}/>
    </button>
  );
}

function CirculosDosMentores({ session, onAbrir, semOMeu = false }: { session: any; onAbrir: (slug: string) => void; semOMeu?: boolean }) {
  const [todos, setMentores] = useState<any[]>([]);
  useEffect(() => {
    if (!session) return;
    let vivo = true;
    api.getMentores(session).then((r) => { if (vivo) setMentores(r?.mentores || []); }).catch(() => {});
    return () => { vivo = false; };
  }, [session?.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const mentores = semOMeu ? todos.filter((m) => !m.sou_o_mentor) : todos;
  if (!mentores.length) return null;
  return (
    <div data-home-circulo="" style={{ padding: '20px 20px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
      {mentores.map((m) => {
        const nome1 = String(m.nome || '').trim().split(/\s+/)[0] || m.nome;
        return (
          <button key={m.slug} data-circulo-slug={m.slug} onClick={() => onAbrir(m.slug)} style={{
            width: '100%', textAlign: 'left', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 13,
            padding: '13px 15px', borderRadius: 18,
            border: '1px solid var(--line)', background: 'var(--paper-2)',
          }}>
            <div style={{
              width: 34, height: 34, borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
              background: 'var(--paper-3)', border: '1px solid var(--ember)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--ember)', fontSize: 15, fontWeight: 600,
            }}>
              {m.avatar_url ? <img src={m.avatar_url} alt="" width={34} height={34} style={{ objectFit: 'cover', width: '100%', height: '100%' }}/> : nome1.slice(0, 1)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="mono" style={{ fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ember)' }}>
                {m.sou_o_mentor ? 'O seu Círculo' : 'Círculo de leitura'}
              </div>
              <div className="serif" style={{ fontSize: 15, lineHeight: 1.2, color: 'var(--ink)', marginTop: 3 }}>Leia junto com {nome1}</div>
              {m.serie?.titulo && (
                <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.serie.titulo}</div>
              )}
            </div>
            <Icon name="chevron-right" size={16} color="var(--ink-3)" stroke={2}/>
          </button>
        );
      })}
    </div>
  );
}

function AnteriormenteEm({ book, session, onAbrir }: { book: BookV2; session: any; onAbrir?: () => void }) {
  const [nota, setNota] = useState<any>(null);
  useEffect(() => {
    if (!session || !book?.id) return;
    let vivo = true;
    api.listarNotas(session, { livro: book.id, limite: '1' })
      .then((r: any) => { if (vivo) setNota((r?.notas || [])[0] || null); })
      .catch(() => { /* sem faixa */ });
    return () => { vivo = false; };
  }, [session, book?.id]);
  const linha = nota ? primeiraLinha(nota) : '';
  if (!nota || !linha) return null;
  const u = unitMeta(book.progressUnit);
  return (
    <button data-anteriormente="1" onClick={onAbrir} style={{
      width: '100%', marginTop: 12, padding: '9px 11px', borderRadius: 12, cursor: onAbrir ? 'pointer' : 'default',
      background: 'var(--paper-2)', border: '1px solid var(--line)', textAlign: 'left',
      display: 'flex', alignItems: 'center', gap: 9,
    }}>
      <Icon name="pen" size={13} color="var(--ember-2)" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="mono" style={{ display: 'flex', gap: 8, fontSize: 8.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ink-4)' }}>
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Anteriormente em {book.title}</span>
          {nota.page != null && <span style={{ flexShrink: 0 }}>{u.short}. {nota.page}</span>}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {linha}
        </div>
      </div>
      <Icon name="chevron-right" size={14} color="var(--ink-4)" />
    </button>
  );
}

function ContinueCard({ book, onOpen, onRead, onStudy, novasMentor = 0, onTravessia, onActions, bookDone: bookDoneFlag, today, mostrarMeta, session }: {
  session?: any;
  book: BookV2;
  onOpen: () => void;
  onRead: () => void;
  onStudy?: () => void;
  novasMentor?: number;
  /** O livro tem A Travessia: a entrada do mapa, com o número das paradas novas. */
  onTravessia?: () => void;
  onActions?: () => void;
  bookDone: boolean;
  today: string;
  mostrarMeta?: boolean;
}) {
  // Com A Travessia, o número das notas novas mora na entrada do mapa, não no caderno.
  const novasCaderno = onTravessia ? 0 : novasMentor;
  const u = unitMeta(book.progressUnit);
  const pct = book.pages > 0 ? Math.round((book.read / book.pages) * 100) : 0;
  const [overlayAberto, setOverlayAberto] = useState(false);

  // FP2 — ETA simples "no ritmo" (7 dias recentes em date-keys SP; suprime >1 ano)
  const _remaining = book.pages - book.read;
  const _sess = book.sessions || [];
  const _pace = ritmoRecente(_sess as any).porDia;
  const _eta = (_pace > 0 && _remaining > 0) ? Math.ceil(_remaining / _pace) : null;
  const etaDays = (_eta != null && _eta <= 365) ? _eta : null;

  // Páginas lidas hoje NESTE livro (sessões com date === today)
  const todayRead = (book.sessions || [])
    .filter(s => s.date === today)
    .reduce((t, s) => t + (s.pages || 0), 0);
  const dailyGoal = book.dailyGoal || 0;
  const dailyHit = dailyGoal > 0 && todayRead >= dailyGoal;
  const bookDone = bookDoneFlag || dailyHit;
  // Tempo de leitura restante / pra meta (precisa da velocidade do Modo Foco; só páginas)
  // Só sessões que mediram leitura (v2/ritmo.ts, pente fino QA-07).
  const _vel = (book.progressUnit || 'pages') === 'pages' ? velocidadePlausivel(_sess as any) : null; // pág/min
  const timeToFinish = _vel ? Math.max(1, Math.round(_remaining / _vel)) : null;
  const toGoalUnits = Math.max(0, dailyGoal - todayRead);
  const timeToGoal = (_vel && dailyGoal > 0 && toGoalUnits > 0) ? Math.max(1, Math.round(toGoalUnits / _vel)) : null;
  const fmtDur = (m: number) => m >= 60 ? `${Math.floor(m / 60)}h${m % 60 ? String(m % 60).padStart(2, '0') : ''}` : `${m}min`;
  const heat = bookHeat(book, today);
  const hui = HEAT_UI[heat.state];

  return (
    <div className="card" data-share-target="card" data-share-bg="#161210" style={{
      padding: 16, position: 'relative', overflow: 'hidden', borderRadius: 20,
      background: bookDone ? 'linear-gradient(135deg, var(--ember-soft), var(--paper-2))' : 'var(--paper-2)',
      borderColor: bookDone ? 'var(--ember)' : 'var(--line)',
      opacity: hui.opacity,
      transition: 'opacity 400ms, background 500ms, border-color 500ms',
    }}>
      {onActions && (
        <button onClick={onActions} aria-label="Mais opções" style={{
          position: 'absolute', top: 8, right: 8, zIndex: 4,
          width: 28, height: 28, borderRadius: '50%', background: 'transparent', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-3)',
        }}>
          <Icon name="dots" size={18} color="var(--ink-3)"/>
        </button>
      )}

      {/* Compartilhar saiu de dentro do menu "..." e virou vizinho dele:
          escondido atrás de um menu, ninguém achava. */}
      <BotaoOverlayStory
        onClick={() => setOverlayAberto(true)}
        top={8} right={onActions ? 40 : 8} caixa={28} fundo="transparent"
      />

      {/* ZONA 1 — identidade: capa + (tag · título · autor) */}
      <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
        <button onClick={onOpen} style={{ background: 'none', flexShrink: 0, filter: hui.filter, transition: 'filter 400ms' }}>
          <BookCover book={book} width={56} height={84} />
        </button>
        <button onClick={onOpen} style={{ flex: 1, minWidth: 0, textAlign: 'left', background: 'none', paddingRight: onActions ? 24 : 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5 }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--ink-3)" strokeWidth={2}><circle cx="12" cy="8" r="3.4"/><path d="M5.5 19a6.5 6.5 0 0 1 13 0"/></svg>
            <span className="mono" style={{ fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-3)', fontWeight: 600 }}>Só você</span>
          </div>
          <div className="serif" style={{ fontSize: 18, lineHeight: 1.14, color: 'var(--ink)',
            overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{book.title}</div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{book.author}</div>
        </button>
      </div>

      {/* divisória */}
      <div style={{ height: 1, background: 'var(--line)', margin: '14px 0 12px' }} />

      {/* ZONA meta — HOJE (só quando "em dia") + calor | posição · % */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
          {/* A meta do livro aparece SEMPRE no modo Leitor. A produção só a
              mostrava com o livro "quente" (lido no último dia), e o efeito era
              perverso: justamente o livro que você abandonou — onde saber a meta
              mais importa — escondia o número.

              No modo Protocolo ela some de propósito: lá o que vence o dia é o
              mínimo ridículo, e uma meta por livro competindo com ele é o mesmo
              problema dos dois placares do mesmo dia se contradizendo. */}
          {mostrarMeta && (
            <div style={{
              position: 'relative', overflow: 'hidden', borderRadius: 8,
              background: dailyHit ? 'transparent' : 'var(--paper-3)',
              border: dailyHit ? '1px solid var(--ember)' : '1px solid transparent',
            }}>
              {dailyGoal > 0 && (
                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${Math.min(1, todayRead / dailyGoal) * 100}%`,
                  background: dailyHit ? 'var(--ember)' : 'rgba(199,83,26,0.16)', transition: 'width 500ms ease' }} />
              )}
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 5, padding: '5px 9px',
                fontFamily: 'Geist Mono, monospace', fontSize: 9.5, letterSpacing: '0.04em',
                color: dailyHit ? 'var(--paper)' : 'var(--ink-2)', whiteSpace: 'nowrap' }}>
                <span style={{ opacity: 0.7 }}>HOJE</span>
                <span style={{ fontWeight: 600 }}>{dailyGoal > 0 ? `${todayRead}/${dailyGoal}` : `${todayRead}`}</span>
                <span style={{ opacity: 0.6 }}>{u.short}</span>
              </div>
            </div>
          )}
          <HeatChip heat={heat} />
        </div>
        <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
          {book.read}/{book.pages} · {pct}%
        </span>
      </div>

      {/* barra de progresso (cor do calor) */}
      <div style={{ height: 5, background: 'var(--paper-3)', borderRadius: 3, overflow: 'hidden', marginTop: 11, position: 'relative' }}>
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, background: hui.bar, borderRadius: 3 }} />
      </div>
      {/* FP2 — ETA "no ritmo" (pequeno, sob a barra) */}
      {etaDays != null && !bookDone && (
        <div className="mono" style={{ fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-4)', marginTop: 6 }}>
          no ritmo · ~{etaDays} {etaDays === 1 ? 'dia' : 'dias'} pra terminar
        </div>
      )}

      <AnteriormenteEm book={book} session={session} onAbrir={onStudy} />

      {onTravessia && (
        <button type="button" onClick={onTravessia} data-travessia-card
          aria-label={novasMentor > 0 ? `A Travessia, ${novasMentor === 1 ? '1 parada nova' : `${novasMentor} paradas novas`}` : 'A Travessia'}
          style={{
            marginTop: 14, width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 12,
            border: `1px solid ${novasMentor > 0 ? 'var(--ember)' : 'var(--line-strong)'}`, background: 'var(--ember-soft)',
            color: 'var(--ink)', fontFamily: 'inherit', textAlign: 'left', cursor: 'pointer',
          }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--ember)" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M3 20c3-1 4-5 7-6s5 2 8-1 2-7 3-9" /><circle cx="21" cy="4" r="1.3" fill="var(--ember)" /></svg>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span className="serif" style={{ fontSize: 16, lineHeight: 1.1 }}>A Travessia</span>
            <span style={{ fontSize: 11.5, color: 'var(--ink-3)', marginLeft: 8 }}>
              {novasMentor > 0 ? (novasMentor === 1 ? 'uma parada nova' : `${novasMentor} paradas novas`) : 'o mapa do livro'}
            </span>
          </span>
          <span style={{ position: 'relative', width: 20, height: 20, flex: 'none', display: 'grid', placeItems: 'center' }}>
            {novasMentor > 0 ? <BadgeMentor n={novasMentor} style={{ top: 0, right: 0 }} /> : <Icon name="arrow-right" size={14} color="var(--ember)" stroke={2}/>}
          </span>
        </button>
      )}

      {/* ZONA ações — tempo restante | CTA  (o compartilhar foi pro menu "...") */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, gap: 10 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          {_vel ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--ink-2)' }}>
                <Icon name="clock" size={12} color="var(--ember-2)" />
                <span><strong>{fmtDur(timeToFinish!)}</strong> pra terminar</span>
              </div>
              <div style={{ fontSize: 10.5, color: dailyHit ? 'var(--ember)' : 'var(--ink-4)', marginTop: 2, paddingLeft: 17 }}>
                {dailyHit ? 'meta de hoje feita ✓' : timeToGoal != null ? `${fmtDur(timeToGoal)} pra meta de hoje` : 'sem meta hoje'}
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--ink-4)' }}>
              <Icon name="clock" size={12} color="var(--ink-4)" />
              <span>Modo Foco mostra o tempo</span>
            </div>
          )}
        </div>
        {onStudy && (
          <button onClick={onStudy} data-caderno-card aria-label={novasCaderno > 0 ? `Caderno de estudos, ${novasCaderno === 1 ? '1 nota nova do Mentor' : `${novasCaderno} notas novas do Mentor`}` : 'Caderno de estudos'} title="Caderno de estudos" style={{
            position: 'relative',
            width: 38, height: 38, flexShrink: 0, borderRadius: '50%', border: `1px solid ${novasCaderno > 0 ? 'var(--ember)' : 'var(--line-strong)'}`,
            background: 'transparent', color: novasCaderno > 0 ? 'var(--ember)' : 'var(--ink-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H11v16H5.5A1.5 1.5 0 0 1 4 18.5z"/><path d="M20 5.5A1.5 1.5 0 0 0 18.5 4H13v16h5.5A1.5 1.5 0 0 0 20 18.5z"/></svg>
            <BadgeMentor n={novasCaderno} />
          </button>
        )}
        <button onClick={onRead} style={{
          background: heat.state === 'cold' ? 'var(--ember-soft)' : (bookDone ? 'var(--ember)' : 'var(--ink)'),
          color: heat.state === 'cold' ? 'var(--ember)' : 'var(--paper)',
          border: heat.state === 'cold' ? '1px solid var(--ember)' : 'none',
          padding: '8px 16px', borderRadius: 999, fontSize: 12.5, fontWeight: 500,
          display: 'flex', alignItems: 'center', gap: 5,
        }}>
          {heat.state === 'cold' ? 'Retomar' : (bookDone ? 'Continuar' : 'Registrar leitura')}
          <Icon name="arrow-right" size={13} color={heat.state === 'cold' ? 'var(--ember)' : 'var(--paper)'} stroke={2}/>
        </button>
      </div>

      <OverlayStoryModal
        aberto={overlayAberto}
        onFechar={() => setOverlayAberto(false)}
        dados={{
          tipo: 'livro',
          titulo: book.title,
          autor: book.author,
          lidoHoje: todayRead,
          metaDoLivro: dailyGoal,
          metaBatida: dailyHit,
          paginaAtual: book.read,
          totalPaginas: book.pages,
          unidadeLonga: u.long,
          capaUrl: (book as any).coverUrl || null,
          // ATENÇÃO: `bookDone` aqui em cima é `bookDoneFlag || dailyHit`, ou
          // seja, meta do DIA. Livro terminado é outra coisa — usar bookDone
          // fazia a peça anunciar "TERMINEI" a cada meta batida.
          terminado: book.pages > 0 && book.read >= book.pages,
        }}
      />
    </div>
  );
}

// Card de livro de Clube/Grupo no Home — distinto visualmente do ContinueCard (livros pessoais).
// Mesma estrutura visual: cover à esquerda, título/autor/progresso e botão "Ler agora"
// (cria espelho pessoal e abre LogReadingSheet). Tap no resto do card → ClubInnerScreen.
function ClubBookCard({ club, term, onOpen, onRead, onActions, today, personalMirror, onOpenProfile }: {
  club: any;
  term: 'clube' | 'grupo';
  onOpen: () => void;
  onRead?: () => void;
  onActions?: () => void;
  today?: string;
  personalMirror?: BookV2 | null;
  onOpenProfile?: (uid: string) => void;
}) {
  // Antes do early return: hook não pode ficar depois de um `return null`.
  const [overlayAberto, setOverlayAberto] = useState(false);

  // Em group-read usa o livro compartilhado; em shared-habit usa o livro
  // pessoal do membro (cada um o seu, mesmo card visual).
  const book = club.book || club.myBook;
  if (!book) return null;
  const ub = unitMeta((personalMirror as any)?.progressUnit || (book as any)?.progressUnit);
  const yourPage = book.yourPage ?? 0;
  const totalPages = book.totalPages ?? 0;

  // Hoje, neste livro do clube — derivado do espelho pessoal do user
  const todayRead = today && personalMirror
    ? (personalMirror.sessions || []).filter(s => s.date === today).reduce((t, s) => t + (s.pages || 0), 0)
    : 0;
  const dailyGoal = personalMirror?.dailyGoal || 0;
  const dailyHit = dailyGoal > 0 && todayRead >= dailyGoal;
  const heat = personalMirror ? bookHeat(personalMirror, today || '') : null;
  const hui = heat ? HEAT_UI[heat.state] : null;
  const previews: any[] = Array.isArray(club.memberPreviews) ? club.memberPreviews : [];

  // FP2 — tempo pra terminar / pra meta (do espelho pessoal; precisa Modo Foco) — igual ao ContinueCard
  const _sess = (personalMirror?.sessions) || [];
  const _remaining = personalMirror ? Math.max(0, (personalMirror.pages || 0) - (personalMirror.read || 0)) : Math.max(0, totalPages - yourPage);
  const _pace = ritmoRecente(_sess as any).porDia;
  const _eta = (_pace > 0 && _remaining > 0) ? Math.ceil(_remaining / _pace) : null;
  const etaDays = (_eta != null && _eta <= 365) ? _eta : null;
  const _vel = ((personalMirror as any)?.progressUnit || 'pages') === 'pages' ? velocidadePlausivel(_sess as any) : null; // pág/min
  const timeToFinish = _vel ? Math.max(1, Math.round(_remaining / _vel)) : null;
  const toGoalUnits = Math.max(0, dailyGoal - todayRead);
  const timeToGoal = (_vel && dailyGoal > 0 && toGoalUnits > 0) ? Math.max(1, Math.round(toGoalUnits / _vel)) : null;
  const fmtDur = (m: number) => m >= 60 ? `${Math.floor(m / 60)}h${m % 60 ? String(m % 60).padStart(2, '0') : ''}` : `${m}min`;

  return (
    <div className="card" data-share-target="card" data-share-bg="#161210" style={{
      padding: 16, position: 'relative', overflow: 'hidden', borderRadius: 20,
      background: 'linear-gradient(135deg, var(--ember-soft), var(--paper-2))',
      border: '1px solid var(--line)',
      opacity: hui ? hui.opacity : 1,
      transition: 'opacity 400ms',
    }}>
      {onActions && (
        <button onClick={onActions} aria-label="Mais opções" style={{
          position: 'absolute', top: 8, right: 8, zIndex: 4,
          width: 28, height: 28, borderRadius: '50%', background: 'transparent', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-3)',
        }}>
          <Icon name="dots" size={18} color="var(--ink-3)"/>
        </button>
      )}

      {/* Compartilhar saiu de dentro do menu "..." e virou vizinho dele:
          escondido atrás de um menu, ninguém achava. */}
      <BotaoOverlayStory
        onClick={() => setOverlayAberto(true)}
        top={8} right={onActions ? 40 : 8} caixa={28} fundo="transparent"
      />

      {/* ZONA 1 — identidade: capa + (CLUBE · nome · título · autor) */}
      <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
        <button onClick={onOpen} style={{ background: 'none', flexShrink: 0, filter: hui ? hui.filter : 'none', transition: 'filter 400ms' }}>
          <BookCover book={{
            title: book.title, author: book.author,
            coverHue: book.coverHue, coverStyle: book.coverStyle, coverUrl: book.coverUrl,
          } as any} width={56} height={84}/>
        </button>
        <button onClick={onOpen} style={{ flex: 1, minWidth: 0, background: 'none', textAlign: 'left', paddingRight: onActions ? 24 : 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5 }}>
            <Icon name="users" size={11} color="var(--ember)" stroke={2}/>
            <span className="mono" style={{ fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ember)', fontWeight: 600,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {term} · {club.name}
            </span>
          </div>
          <div className="serif" style={{ fontSize: 18, lineHeight: 1.14, color: 'var(--ink)',
            overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {book.title}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{book.author}</div>
        </button>
      </div>

      {/* divisória */}
      <div style={{ height: 1, background: 'var(--line)', margin: '14px 0 12px' }} />

      {/* ZONA meta — HOJE (se membro lê hoje) + calor | posição */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
          {personalMirror && heat && heat.state === 'hot' && (
            <div style={{
              position: 'relative', overflow: 'hidden', borderRadius: 8,
              background: dailyHit ? 'transparent' : 'var(--paper-3)',
              border: dailyHit ? '1px solid var(--ember)' : '1px solid transparent',
            }}>
              {dailyGoal > 0 && (
                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${Math.min(1, todayRead / dailyGoal) * 100}%`, background: dailyHit ? 'var(--ember)' : 'rgba(199,83,26,0.16)' }} />
              )}
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 5, padding: '5px 9px',
                fontFamily: 'Geist Mono, monospace', fontSize: 9.5, letterSpacing: '0.04em',
                color: dailyHit ? 'var(--paper)' : 'var(--ink-2)', whiteSpace: 'nowrap' }}>
                <span style={{ opacity: 0.7 }}>HOJE</span>
                <span style={{ fontWeight: 600 }}>{dailyGoal > 0 ? `${todayRead}/${dailyGoal}` : `${todayRead}`}</span>
                <span style={{ opacity: 0.6 }}>{ub.short}</span>
              </div>
            </div>
          )}
          {heat ? <HeatChip heat={heat} /> : <span />}
        </div>
        {totalPages > 0 && (
          <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{yourPage} / {totalPages}</span>
        )}
      </div>

      {/* membros (avatares + contagem) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0, marginTop: 14 }}>
        {previews.length > 0 && (
          <div style={{ display: 'flex' }}>
            {previews.slice(0, 3).map((m: any, i: number) => (
              <div key={i} onClick={(e) => { if (onOpenProfile && m.id) { e.stopPropagation(); onOpenProfile(m.id); } }} style={{
                width: 22, height: 22, borderRadius: '50%', marginLeft: i === 0 ? 0 : -7,
                border: '1.5px solid var(--paper-2)', overflow: 'hidden', flexShrink: 0,
                background: 'var(--paper-3)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 9, fontWeight: 600, color: 'var(--ink-2)', fontFamily: 'Geist Mono, monospace',
                cursor: (onOpenProfile && m.id) ? 'pointer' : 'default',
              }}>
                {m.avatarUrl ? <img src={m.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}/> : (m.initial || '?')}
              </div>
            ))}
          </div>
        )}
        <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
          {(club.members ?? 0)} {(club.members ?? 0) === 1 ? 'membro' : 'membros'}
        </span>
      </div>

      {/* FP2 — ETA "no ritmo" (igual ao card pessoal) */}
      {etaDays != null && (
        <div className="mono" style={{ fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-4)', marginTop: 8 }}>
          no ritmo · ~{etaDays} {etaDays === 1 ? 'dia' : 'dias'} pra terminar
        </div>
      )}

      {/* ZONA ações — tempo (igual ao card pessoal) | Ler agora (o compartilhar foi pro menu "...") */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, gap: 10 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          {_vel ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--ink-2)' }}>
                <Icon name="clock" size={12} color="var(--ember-2)" />
                <span><strong>{fmtDur(timeToFinish!)}</strong> pra terminar</span>
              </div>
              <div style={{ fontSize: 10.5, color: dailyHit ? 'var(--ember)' : 'var(--ink-4)', marginTop: 2, paddingLeft: 17 }}>
                {dailyHit ? 'meta de hoje feita ✓' : timeToGoal != null ? `${fmtDur(timeToGoal)} pra meta de hoje` : 'sem meta hoje'}
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--ink-4)' }}>
              <Icon name="clock" size={12} color="var(--ink-4)" />
              <span>Modo Foco mostra o tempo</span>
            </div>
          )}
        </div>
        {onRead && (
          <button onClick={onRead} style={{
            background: heat && heat.state === 'cold' ? 'var(--ember-soft)' : 'var(--ink)',
            color: heat && heat.state === 'cold' ? 'var(--ember)' : 'var(--paper)',
            border: heat && heat.state === 'cold' ? '1px solid var(--ember)' : 'none',
            padding: '8px 16px', borderRadius: 999, fontSize: 12.5, fontWeight: 500,
            display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0,
          }}>
            {heat && heat.state === 'cold' ? 'Retomar' : 'Registrar leitura'}
            <Icon name="arrow-right" size={13} color={heat && heat.state === 'cold' ? 'var(--ember)' : 'var(--paper)'} stroke={2}/>
          </button>
        )}
      </div>

      {/* No clube o progresso que importa é o SEU, não o do livro coletivo. */}
      <OverlayStoryModal
        aberto={overlayAberto}
        onFechar={() => setOverlayAberto(false)}
        dados={{
          tipo: 'livro',
          titulo: book.title,
          autor: (book as any).author,
          lidoHoje: todayRead,
          metaDoLivro: dailyGoal,
          metaBatida: dailyHit,
          paginaAtual: yourPage,
          totalPaginas: totalPages,
          unidadeLonga: ub.long,
          capaUrl: (book as any).coverUrl || (personalMirror as any)?.coverUrl || null,
          terminado: totalPages > 0 && yourPage >= totalPages,
        }}
      />
    </div>
  );
}

